import { useMemo, useRef, useState } from "react";

import { interpolateRgb, scaleSequential } from "d3";

import { formatWeekRange } from "@/lib/weekDates";
import { cn } from "@/lib/utils";

import { ChartTooltip } from "./ChartTooltip";
import {
  colorForD3Interpolation,
  heatmapTextOnFill,
  useChartTheme,
} from "./theme";
import type {
  ChartInteraction,
  ChartMask,
  ChartVariant,
  HeatmapChartSpec,
} from "./types";
import {
  formatSeriesValue,
  useContainerSize,
  VARIANT_CONFIG,
} from "./useChartModel";

const DOW_FULL: Record<string, string> = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

interface HeatmapChartProps extends ChartInteraction {
  spec: HeatmapChartSpec;
  variant?: ChartVariant;
  mask?: ChartMask;
}

interface HoveredCell {
  row: number;
  col: number;
  x: number;
  y: number;
}

/**
 * Cohort color-map: rows × weekday columns, sequential heat so busy days
 * read as hot and quiet days stay pale. Cell / header clicks feed cross-filters.
 */
export function HeatmapChart({
  spec,
  variant = "default",
  mask,
  onCategoryClick,
  onSeriesClick,
  onCellClick,
  selectedCategory,
  selectedSeriesKey,
}: HeatmapChartProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const fallback = VARIANT_CONFIG[variant].height;
  const size = useContainerSize(rootRef, { width: 320, height: fallback });
  const theme = useChartTheme();
  const [hover, setHover] = useState<HoveredCell | null>(null);

  const max = useMemo(() => {
    let peak = 0;
    for (const row of spec.values) {
      for (const value of row) peak = Math.max(peak, value);
    }
    return peak;
  }, [spec.values]);

  const color = useMemo(() => {
    const primaryPeak = colorForD3Interpolation(
      theme.series[0] ?? theme.heatmapFillMax,
      theme.heatmapFillMax,
    );
    const start = colorForD3Interpolation(theme.heatmapFillMin, "#eef0fa");
    const end = colorForD3Interpolation(theme.heatmapFillMax, primaryPeak);
    return scaleSequential(interpolateRgb(start, end)).domain([0, max || 1]);
  }, [max, theme.heatmapFillMax, theme.heatmapFillMin, theme.series]);

  const { rowTotals, colTotals, grandTotal } = useMemo(() => {
    const rows = spec.values.length;
    const cols = spec.columns.length;
    const rowTotals: number[] = Array.from({ length: rows }, () => 0);
    const colTotals: number[] = Array.from({ length: cols }, () => 0);
    let grandTotal = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const v = spec.values[r]?.[c] ?? 0;
        rowTotals[r] += v;
        colTotals[c] += v;
        grandTotal += v;
      }
    }
    return { rowTotals, colTotals, grandTotal };
  }, [spec.columns.length, spec.values]);

  const showInCell =
    size.width / (spec.columns.length + 2) >= 28;

  const clickable = Boolean(onCategoryClick || onSeriesClick);

  const marginalCellClass =
    "flex min-h-4 items-center justify-end rounded-[3px] bg-muted/35 px-1 text-[10px] font-medium tabular-nums leading-none text-foreground ring-1 ring-border/40";

  const marginalHeaderClass =
    "truncate px-1 pb-1 text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground";
  const hovered =
    hover && spec.rows[hover.row] != null
      ? {
          rowLabel: spec.rows[hover.row],
          colLabel: spec.columns[hover.col] ?? "",
          value: spec.values[hover.row]?.[hover.col] ?? 0,
        }
      : null;

  const handleCellClick = (row: number, col: number) => {
    const rowKey = spec.rowKeys[row] ?? spec.rows[row];
    const colKey = spec.columnKeys[col] ?? spec.columns[col];
    if (onCellClick && rowKey && colKey) {
      onCellClick({
        category: spec.rows[row],
        categoryKey: rowKey,
        title: spec.columns[col],
        seriesKey: colKey,
        index: col,
      });
      return;
    }
    if (onCategoryClick && rowKey) {
      onCategoryClick({ category: spec.rows[row], categoryKey: rowKey });
    }
    if (onSeriesClick && colKey) {
      onSeriesClick({ title: spec.columns[col], seriesKey: colKey, index: col });
    }
  };

  return (
    <div ref={rootRef} className="flex h-full min-h-0 w-full flex-col gap-1.5">
      <div
        className="grid min-h-0 min-w-0 flex-1"
        style={{
          gridTemplateColumns: `minmax(3.25rem, max-content) repeat(${spec.columns.length}, minmax(0, 1fr)) minmax(2.25rem, max-content)`,
          gridTemplateRows: `auto repeat(${spec.rows.length}, minmax(0, 1fr)) auto`,
          columnGap: "0.25rem",
          rowGap: "0.25rem",
          width: size.width > 0 ? size.width : "100%",
        }}
        role="img"
        aria-label={spec.title ?? "Heatmap"}
      >
        <div />
        {spec.columns.map((col, colIndex) => {
          const selected = selectedSeriesKey === (spec.columnKeys[colIndex] ?? col);
          return (
            <button
              key={col}
              type="button"
              disabled={!onSeriesClick}
              onClick={() =>
                onSeriesClick?.({
                  title: col,
                  seriesKey: spec.columnKeys[colIndex] ?? col,
                  index: colIndex,
                })
              }
              className={cn(
                "truncate px-0.5 pb-1 text-center text-[10px] font-medium text-muted-foreground",
                onSeriesClick && "cursor-pointer hover:text-foreground",
                selected && "text-foreground underline decoration-foreground/40",
              )}
            >
              {col}
            </button>
          );
        })}
        <div
          className={cn(marginalHeaderClass, "border-l border-border/50 pl-2")}
          aria-hidden
        >
          Σ
        </div>

        {spec.rows.map((row, rowIndex) => {
          const rowSelected =
            selectedCategory === row ||
            selectedCategory === spec.rowKeys[rowIndex];
          return [
            <button
              key={`row-${row}`}
              type="button"
              disabled={!onCategoryClick}
              onClick={() =>
                onCategoryClick?.({
                  category: row,
                  categoryKey: spec.rowKeys[rowIndex] ?? row,
                })
              }
              className={cn(
                "truncate pr-1.5 text-left text-[10px] tabular-nums text-muted-foreground",
                mask?.category && "sensitive-blur",
                onCategoryClick && "cursor-pointer hover:text-foreground",
                rowSelected && "font-semibold text-foreground",
              )}
              title={formatWeekRange(row) ?? row}
            >
              {row}
            </button>,
            ...spec.columns.map((col, colIndex) => {
              const value = spec.values[rowIndex]?.[colIndex] ?? 0;
              const colSelected =
                selectedSeriesKey === (spec.columnKeys[colIndex] ?? col);
              const isHover =
                hover?.row === rowIndex && hover?.col === colIndex;
              const emphasis = rowSelected || colSelected || isHover;
              const fill = color(value);
              return (
                <button
                  key={`${row}-${col}`}
                  type="button"
                  disabled={!clickable}
                  className={cn(
                    "flex min-h-4 items-center justify-center rounded-[3px] border border-transparent text-[10px] tabular-nums leading-none",
                    value > 0 && showInCell && "font-semibold",
                    clickable && "cursor-pointer",
                    emphasis && "ring-1 ring-foreground/40",
                    mask?.value && value > 0 && "sensitive-blur",
                  )}
                  style={{
                    backgroundColor: fill,
                    color: heatmapTextOnFill(fill),
                    opacity: value === 0 && !emphasis ? 0.5 : 1,
                  }}
                  onMouseEnter={(event) =>
                    setHover({
                      row: rowIndex,
                      col: colIndex,
                      x: event.clientX,
                      y: event.clientY,
                    })
                  }
                  onMouseMove={(event) =>
                    setHover({
                      row: rowIndex,
                      col: colIndex,
                      x: event.clientX,
                      y: event.clientY,
                    })
                  }
                  onMouseLeave={() => setHover(null)}
                  onClick={() => handleCellClick(rowIndex, colIndex)}
                >
                  {value > 0 && showInCell
                    ? formatSeriesValue(value, spec.valueFormat)
                    : ""}
                </button>
              );
            }),
            <div
              key={`row-total-${row}`}
              className={cn(
                marginalCellClass,
                "border-l border-border/50 pl-2",
                mask?.value && rowTotals[rowIndex] > 0 && "sensitive-blur",
              )}
              aria-label={`Total for ${row}`}
            >
              {formatSeriesValue(rowTotals[rowIndex], spec.valueFormat)}
            </div>,
          ];
        })}

        <div
          className={cn(
            "truncate pt-1 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground",
            "border-t border-border/50",
          )}
        >
          Σ
        </div>
        {spec.columns.map((col, colIndex) => {
          const total = colTotals[colIndex] ?? 0;
          return (
            <div
              key={`col-total-${col}`}
              className={cn(
                marginalCellClass,
                "justify-center border-t border-border/50 pt-1",
                mask?.value && total > 0 && "sensitive-blur",
              )}
              aria-label={`Total for ${col}`}
            >
              {formatSeriesValue(total, spec.valueFormat)}
            </div>
          );
        })}
        <div
          className={cn(
            marginalCellClass,
            "border-l border-t border-border/50 pl-2 pt-1",
            mask?.value && grandTotal > 0 && "sensitive-blur",
          )}
          aria-label="Grand total"
        >
          {formatSeriesValue(grandTotal, spec.valueFormat)}
        </div>
      </div>

      {max > 0 && (
        <div className="flex items-center gap-2 px-0.5">
          <span className="text-[10px] text-muted-foreground">Quiet</span>
          <div
            className="h-1.5 flex-1 rounded-full"
            style={{
              background: `linear-gradient(90deg, ${color(0)}, ${color(max)})`,
            }}
          />
          <span className="text-[10px] text-muted-foreground">Hectic</span>
        </div>
      )}

      {hovered && (
        <ChartTooltip
          anchorX={hover?.x ?? 0}
          anchorY={hover?.y ?? 0}
          title={formatWeekRange(hovered.rowLabel) ?? hovered.rowLabel}
          rows={[
            {
              label: DOW_FULL[hovered.colLabel] ?? hovered.colLabel,
              value: formatSeriesValue(hovered.value, spec.valueFormat),
              color: color(hovered.value),
            },
          ]}
          mask={mask}
          hint={clickable ? "Click to filter" : undefined}
        />
      )}
    </div>
  );
}
